import { useGrantEquipmentExceptionMutation, useRemoveEquipmentExceptionMutation } from '@/services/accounts';
import { AvlItem, useGetAvlItemsQuery } from '@/services/approvedVendorList';
import {
  Box,
  BoxProps,
  Button,
  FormikCheckboxInput,
  FormikSelectInputNative,
  FormikTextInput,
  SelectInput,
  SimulatedEventPayloadType,
  TextLink,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useEffect, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';

import {
  ApprovedVendorAllocation,
  AvlFilterOption,
  EquipmentException,
  EquipmentExceptionAllowedProgramTypes,
  FeocDataItem,
  ProgramType,
  UserPermissions,
  VendorEquipmentTypes,
} from 'types';

import { GrantEquipmentExceptionModal } from '../Account/GrantEquipmentExceptionModal';
import RequirePermissions from '../auth/requirePermissions';
import FormSection from '../Forms/FormSection';
import { EquipmentDetailsDescription } from '../InstallMilestonePackage/InstallationFormSectionDescriptions';

import {
  filterDcEligibleAvlItems,
  mapNonDcAvlManufacturerOptions,
  mapAvlModelNumberOptions,
  maybePrependCustomFreeTextOption,
  resolveSelectedOption,
  Option as SelectOption,
} from './AvlFormUtils';
import { ModelSelectChangeEvent, SystemAssetModel } from './SystemAssetModel';

export const findMatchingEquipment = (
  avlEquipment: AvlItem[],
  manufacturer: string,
  model: string,
  avlItemId?: string,
): AvlItem | undefined => {
  if (avlItemId) {
    const matchById = avlEquipment.find((item) => item.id === avlItemId);
    if (matchById) return matchById;
  }

  if (!model || !manufacturer) return undefined;

  // Exact match must always win over an alias match, regardless of array order —
  // an earlier item's stale alias (captured on rename) should not shadow a later
  // item's genuine, current name/modelNumber match.
  const exactMatch = avlEquipment.find(
    (item) => item.manufacturer === manufacturer && (item.modelNumber === model || item.name === model),
  );
  if (exactMatch) return exactMatch;

  return avlEquipment.find(
    (item) =>
      item.manufacturer === manufacturer &&
      item.aliases?.some((alias) => alias.value.toLowerCase() === model.toLowerCase()),
  );
};

const isSafeHarborWithSingleSubSku = (equipment: AvlItem | undefined): boolean => {
  if (!equipment) return false;
  return equipment.allocation === ApprovedVendorAllocation.safeHarbor && equipment.feocData?.length === 1;
};

const getSingleSafeHarborVintage = (equipment: AvlItem | undefined): string | undefined => {
  if (!isSafeHarborWithSingleSubSku(equipment)) return undefined;
  return equipment!.feocData![0].vintage;
};

export const getVintageOptions = (
  avlEquipment: AvlItem[],
  manufacturer: string,
  model: string,
  avlItemId?: string,
) => {
  const baseOptions = [{ label: 'None', value: 'none' }];
  if (!model || !manufacturer) {
    return baseOptions;
  }

  const matchingEquipment = findMatchingEquipment(avlEquipment, manufacturer, model, avlItemId);
  if (!matchingEquipment?.feocData || matchingEquipment.feocData.length === 0) {
    return baseOptions;
  }

  // For Safe Harbor equipment with single sub-SKU, return only that option (no "None")
  if (isSafeHarborWithSingleSubSku(matchingEquipment)) {
    return [{ label: matchingEquipment.feocData[0].vintage, value: matchingEquipment.feocData[0].vintage }];
  }

  const vintages = matchingEquipment.feocData.map((item: FeocDataItem) => ({
    label: item.vintage,
    value: item.vintage,
  }));
  return baseOptions.concat(vintages);
};

interface SyncSafeHarborVintageParams {
  models?: { model: string; vintage?: string; avlItemId?: string }[];
  avlEquipment?: AvlItem[];
  manufacturer?: string;
  fieldName: 'inverterModels' | 'storageModels';
  setFieldValue: (field: string, value: any) => void;
}

export const syncSafeHarborVintage = ({
  models,
  avlEquipment,
  manufacturer,
  fieldName,
  setFieldValue,
}: SyncSafeHarborVintageParams) => {
  if (!models || !avlEquipment?.length || !manufacturer) return;

  models.forEach((equipment, index) => {
    if (!equipment.model) return;

    const matchingEquipment = findMatchingEquipment(
      avlEquipment,
      manufacturer,
      equipment.model,
      equipment.avlItemId,
    );
    const singleVintage = getSingleSafeHarborVintage(matchingEquipment);
    const hasVintageOptions = !!matchingEquipment?.feocData?.length;

    if (singleVintage && equipment.vintage !== singleVintage) {
      setFieldValue(`${fieldName}.${index}.vintage`, singleVintage);
      return;
    }

    if (matchingEquipment && !hasVintageOptions && equipment.vintage && equipment.vintage !== 'none') {
      setFieldValue(`${fieldName}.${index}.vintage`, 'none');
    }
  });
};

interface EnphaseAcknowledgmentCheckboxProps {
  fieldName: string;
  componentType: 'inverter' | 'battery';
  isDisabled: boolean;
  onCheckboxChange: (fieldName: string, checked: boolean) => void;
}

const EnphaseAcknowledgmentCheckbox = ({
  fieldName,
  componentType,
  isDisabled,
  onCheckboxChange,
}: EnphaseAcknowledgmentCheckboxProps) => {
  const componentLabel = componentType === 'inverter' ? 'Inverters' : 'Batteries';
  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }} direction="row" margin="lg 0 lg 0">
      <Field
        label={
          <>
            I confirm that this system uses {componentLabel} manufactured after November 10, 2025 and will contribute
            non-FEOC value. For more information, and to check component eligibility, visit the{' '}
            <TextLink href="https://enphase.com/support/feoc-compliance" target="_blank" rel="noopener noreferrer">
              Enphase website
            </TextLink>
            , and log into your account to access their &ldquo;FEOC Compliance&rdquo; tool.
          </>
        }
        name={fieldName}
        id={fieldName}
        component={FormikCheckboxInput}
        isDisabled={isDisabled}
        onChange={(e: any) => onCheckboxChange(fieldName, e.target.checked)}
      />
    </Box>
  );
};

interface EquipmentDetailsProps {
  hideTitleAndDescription?: boolean;
  hideQuantity?: boolean;
  showStorageOptions?: boolean;
  allowMultipleInverters?: boolean;
  allowMultipleBatteries?: boolean;
  isLoading?: boolean;
  boxProps?: BoxProps;
  programType: ProgramType;
  avlFilterOption?: AvlFilterOption;
  isOptionalForNtp?: boolean;
  showOptimizerCheckbox?: boolean;
  showEnphaseCheckboxes?: boolean;
  readOnly?: boolean;
  removeModelWhenManufacturerChanges?: boolean;
  equipmentException?: EquipmentException | null;
  orgAlias?: string;
  isPackageEditable?: boolean;
  useSearchableModelSelect?: boolean;
}

export const EquipmentDetailsFormSection = ({
  hideTitleAndDescription = false,
  hideQuantity = false,
  showStorageOptions = false,
  allowMultipleInverters = true,
  allowMultipleBatteries = false,
  isLoading,
  programType,
  avlFilterOption,
  isOptionalForNtp = true,
  showOptimizerCheckbox = false,
  showEnphaseCheckboxes = false,
  readOnly = false,
  removeModelWhenManufacturerChanges = false,
  equipmentException,
  orgAlias,
  isPackageEditable = true,
  useSearchableModelSelect = false,
}: EquipmentDetailsProps) => {
  const { isSubmitting, values, setFieldTouched, setFieldValue } = useFormikContext<any>();
  const { id: accountId } = useParams<{ id: string }>();
  const { feocDcCalculator } = useFlags();
  const { inverterModels, storageModels } = values;
  const {
    isOpen: isExceptionModalOpen,
    handleOpen: openExceptionModal,
    handleClose: closeExceptionModal,
  } = useOpenClose();
  const [grantException] = useGrantEquipmentExceptionMutation();
  const [removeException] = useRemoveEquipmentExceptionMutation();

  const handleGrantException = useCallback(
    async (data: { equipmentTypes: VendorEquipmentTypes[]; note?: string }) => {
      if (!accountId) return;
      await grantException({ accountId, equipmentTypes: data.equipmentTypes, note: data.note }).unwrap();
    },
    [accountId, grantException],
  );

  const handleRemoveException = useCallback(async () => {
    if (!accountId) return;
    await removeException({ accountId }).unwrap();
  }, [accountId, removeException]);

  // One query per equipment type since V2 no longer returns the whole ApprovedVendorSettings map in a single call.
  const avlScopeParams = accountId ? { accountId } : { programType, orgAlias };
  const { data: panelData, isLoading: isPanelLoading } = useGetAvlItemsQuery({
    ...avlScopeParams,
    equipmentType: VendorEquipmentTypes.panel,
  });
  const { data: inverterData, isLoading: isInverterLoading } = useGetAvlItemsQuery({
    ...avlScopeParams,
    equipmentType: VendorEquipmentTypes.inverter,
  });
  const { data: storageData, isLoading: isStorageLoading } = useGetAvlItemsQuery({
    ...avlScopeParams,
    equipmentType: VendorEquipmentTypes.storage,
  });

  const isAppSettingsLoading = isPanelLoading || isInverterLoading || isStorageLoading;

  // V2 has no server-side avlFilterOption; dcEligibleOnly is applied here instead.
  const avlPanels = useMemo(() => {
    const items = panelData?.data ?? [];
    return avlFilterOption === AvlFilterOption.dcEligibleOnly
      ? filterDcEligibleAvlItems(items, VendorEquipmentTypes.panel)
      : items;
  }, [panelData, avlFilterOption]);
  const avlInverters = useMemo(() => {
    const items = inverterData?.data ?? [];
    return avlFilterOption === AvlFilterOption.dcEligibleOnly
      ? filterDcEligibleAvlItems(items, VendorEquipmentTypes.inverter)
      : items;
  }, [inverterData, avlFilterOption]);
  const avlStorage = useMemo(() => {
    const items = storageData?.data ?? [];
    return avlFilterOption === AvlFilterOption.dcEligibleOnly
      ? filterDcEligibleAvlItems(items, VendorEquipmentTypes.storage)
      : items;
  }, [storageData, avlFilterOption]);

  const isAnythingLoadingOrSubmitting = isLoading || isAppSettingsLoading || isSubmitting;

  const inverterManufacturerOptions = useMemo(() => {
    const options = mapNonDcAvlManufacturerOptions(avlInverters);
    return maybePrependCustomFreeTextOption(options, values.inverterManufacturer);
  }, [avlInverters, values.inverterManufacturer]);

  const inverterModelOptions = useMemo(() => {
    return (
      inverterModels?.map((inverter: any) => {
        const options = mapAvlModelNumberOptions(avlInverters, values.inverterManufacturer);
        return maybePrependCustomFreeTextOption(options, inverter.model);
      }) ?? []
    );
  }, [avlInverters, inverterModels, values.inverterManufacturer]);

  const inverterVintageOptions = useMemo(() => {
    return (
      inverterModels?.map((inverter: any) =>
        getVintageOptions(avlInverters, values.inverterManufacturer, inverter.model, inverter.avlItemId),
      ) ?? []
    );
  }, [avlInverters, inverterModels, values.inverterManufacturer]);

  const panelManufacturerOptions = useMemo(() => {
    const options = mapNonDcAvlManufacturerOptions(avlPanels);
    return maybePrependCustomFreeTextOption(options, values.panelManufacturer);
  }, [avlPanels, values.panelManufacturer]);

  const panelModelOptions = useMemo(() => {
    const options = mapAvlModelNumberOptions(avlPanels, values.panelManufacturer);
    return maybePrependCustomFreeTextOption(options, values.panelModel);
  }, [avlPanels, values.panelManufacturer, values.panelModel]);

  const panelVintageOptions = useMemo(() => {
    return getVintageOptions(avlPanels, values.panelManufacturer, values.panelModel);
  }, [avlPanels, values.panelManufacturer, values.panelModel]);

  const storageManufacturerOptions = useMemo(() => {
    const options = mapNonDcAvlManufacturerOptions(avlStorage);
    return maybePrependCustomFreeTextOption(options, values.storageManufacturer);
  }, [avlStorage, values.storageManufacturer]);

  const storageModelOptions = useMemo(() => {
    return (
      storageModels?.map((storage: any) => {
        const options = mapAvlModelNumberOptions(avlStorage, values.storageManufacturer);
        return maybePrependCustomFreeTextOption(options, storage.model);
      }) ?? []
    );
  }, [avlStorage, values.storageManufacturer, storageModels]);

  const storageVintageOptions = useMemo(() => {
    return (
      storageModels?.map((storage: any) =>
        getVintageOptions(avlStorage, values.storageManufacturer, storage.model, storage.avlItemId),
      ) ?? []
    );
  }, [avlStorage, values.storageManufacturer, storageModels]);

  const solarEdgeSelected = values.inverterManufacturer === 'SolarEdge';
  const enphaseInverterSelected = values.inverterManufacturer === 'Enphase';
  const enphaseStorageSelected = values.storageManufacturer === 'Enphase';

  const addInverter = useCallback(() => {
    setFieldValue('inverterModels', [...(inverterModels ?? []), { model: '', count: 1, vintage: 'none' }]);
    setFieldTouched('inverterModels', true);
  }, [inverterModels, setFieldValue, setFieldTouched]);

  const addStorage = useCallback(() => {
    setFieldValue('storageModels', [...(storageModels ?? []), { model: '', count: 1, vintage: 'none' }]);
    setFieldTouched('storageModels', true);
  }, [storageModels, setFieldValue, setFieldTouched]);

  const removeInverter = useCallback(
    (index: number) => {
      const newInverterModels = (inverterModels ?? []).filter((_: any, i: number) => i !== index);
      setFieldValue('inverterModels', newInverterModels);
      setFieldTouched('inverterModels', true);
    },
    [inverterModels, setFieldValue, setFieldTouched],
  );

  const removeStorage = useCallback(
    (index: number) => {
      const newStorageModels = (storageModels ?? []).filter((_: any, i: number) => i !== index);
      setFieldValue('storageModels', newStorageModels);
      setFieldTouched('storageModels', true);
      if (newStorageModels.length === 0) {
        setFieldValue('storageManufacturer', '');
      }
    },
    [storageModels, setFieldValue, setFieldTouched],
  );

  const setQuantityValue = useCallback(
    (fieldId: string, event: ChangeEvent<HTMLInputElement>) => {
      const quantity = Number(event.target.value);
      if (!Number.isNaN(quantity)) {
        setFieldValue(fieldId, quantity);
      }
    },
    [setFieldValue],
  );

  // Picking a different model must invalidate the previously-resolved avlItemId for that slot —
  // otherwise findMatchingEquipment's id-first match keeps resolving to the old equipment.
  // event.target.value is a plain string from the native select, or the selected
  // { label, value } option object from the searchable (react-select) variant.
  const setModelValue = useCallback(
    (fieldPath: string, event: ModelSelectChangeEvent) => {
      const rawValue = event.target.value;
      const newValue = typeof rawValue === 'object' ? ((rawValue as SelectOption)?.value ?? '') : rawValue;
      setFieldValue(fieldPath, newValue);
      setFieldValue(fieldPath.replace(/\.model$/, '.avlItemId'), undefined);
    },
    [setFieldValue],
  );

  const handlePanelModelChange = useCallback(
    (event: SimulatedEventPayloadType) => setFieldValue('panelModel', event.target.value?.value ?? ''),
    [setFieldValue],
  );

  const handlePanelModelBlur = useCallback(() => setFieldTouched('panelModel', true), [setFieldTouched]);

  const renderInverterModels = useCallback(
    () =>
      inverterModels?.map((inverter: { model: string }, index: number) => (
        <SystemAssetModel
          key={`inverter-${index}`}
          formPath="inverterModels"
          index={index}
          modelLabel={(i) => `Inverter ${i > 0 ? i + 1 : 'Model'}`}
          modelOptions={inverterModelOptions[index]}
          modelValue={inverter.model}
          useSearchableModelSelect={useSearchableModelSelect}
          showVintage={feocDcCalculator && inverterVintageOptions?.[index]?.length > 1}
          vintageLabel={(i) => `Inverter ${i > 0 ? i + 1 : 'Vintage'}`}
          vintageOptions={inverterVintageOptions?.[index] || [{ label: 'None', value: 'none' }]}
          hideQuantity={hideQuantity}
          isDisabled={
            isAnythingLoadingOrSubmitting ||
            readOnly ||
            !isPackageEditable ||
            (useSearchableModelSelect && !values.inverterManufacturer)
          }
          onRemove={() => removeInverter(index)}
          onQuantityChange={setQuantityValue}
          onModelChange={setModelValue}
        />
      )),
    [
      feocDcCalculator,
      hideQuantity,
      inverterModelOptions,
      inverterModels,
      inverterVintageOptions,
      isAnythingLoadingOrSubmitting,
      readOnly,
      isPackageEditable,
      removeInverter,
      setQuantityValue,
      setModelValue,
      useSearchableModelSelect,
      values.inverterManufacturer,
    ],
  );

  const renderStorageModels = useCallback(
    () =>
      storageModels?.map((storage: { model: string }, index: number) => {
        return (
          <SystemAssetModel
            key={`storage-${index}`}
            formPath="storageModels"
            index={index}
            modelLabel={(i) => `Storage ${i > 0 ? i + 1 : 'Model'}`}
            modelOptions={storageModelOptions[index]}
            modelValue={storage.model}
            useSearchableModelSelect={useSearchableModelSelect}
            showVintage={feocDcCalculator && storageVintageOptions?.[index]?.length > 1}
            vintageLabel={(i) => `Storage ${i > 0 ? i + 1 : 'Vintage'}`}
            vintageOptions={storageVintageOptions?.[index] || [{ label: 'None', value: 'none' }]}
            hideQuantity={hideQuantity}
            isDisabled={
              isAnythingLoadingOrSubmitting ||
              readOnly ||
              !isPackageEditable ||
              (useSearchableModelSelect && !values.storageManufacturer)
            }
            onRemove={() => removeStorage(index)}
            allowRemoveFirst
            onQuantityChange={setQuantityValue}
            onModelChange={setModelValue}
          />
        );
      }),
    [
      feocDcCalculator,
      hideQuantity,
      storageModelOptions,
      storageModels,
      storageVintageOptions,
      isAnythingLoadingOrSubmitting,
      readOnly,
      isPackageEditable,
      removeStorage,
      setQuantityValue,
      setModelValue,
      useSearchableModelSelect,
      values.storageManufacturer,
    ],
  );

  useEffect(() => {
    setFieldTouched('panelModel', true);
  }, [values.panelManufacturer, setFieldTouched]);

  useEffect(() => {
    if (removeModelWhenManufacturerChanges) {
      setFieldValue('panelModel', '');
      setFieldValue('panelVintage', '');
    }
  }, [values.panelManufacturer, removeModelWhenManufacturerChanges, setFieldValue]);

  useEffect(() => {
    if (removeModelWhenManufacturerChanges) {
      setFieldValue('inverterModels', [{ model: '', count: 1, vintage: 'none' }]);
    }
  }, [values.inverterManufacturer, removeModelWhenManufacturerChanges, setFieldValue]);

  useEffect(() => {
    if (removeModelWhenManufacturerChanges) {
      setFieldValue('storageModels', [{ model: '', count: 1, vintage: 'none' }]);
    }
  }, [values.storageManufacturer, removeModelWhenManufacturerChanges, setFieldValue]);

  // Auto-update vintage for Safe Harbor inverters with single sub-SKU
  useEffect(() => {
    if (readOnly) return;
    if (!inverterModels || !avlInverters?.length) return;

    syncSafeHarborVintage({
      models: inverterModels,
      avlEquipment: avlInverters,
      manufacturer: values.inverterManufacturer,
      fieldName: 'inverterModels',
      setFieldValue,
    });
  }, [readOnly, inverterModels, avlInverters, values.inverterManufacturer, setFieldValue]);

  // Auto-update vintage for Safe Harbor storage with single sub-SKU
  useEffect(() => {
    if (readOnly) return;
    if (!storageModels || !avlStorage?.length) return;

    syncSafeHarborVintage({
      models: storageModels,
      avlEquipment: avlStorage,
      manufacturer: values.storageManufacturer,
      fieldName: 'storageModels',
      setFieldValue,
    });
  }, [readOnly, storageModels, avlStorage, values.storageManufacturer, setFieldValue]);

  return (
    <FormSection
      {...(!hideTitleAndDescription && {
        title: 'Equipment',
        description: EquipmentDetailsDescription(isOptionalForNtp),
      })}
      {...(!hideTitleAndDescription &&
        accountId &&
        EquipmentExceptionAllowedProgramTypes.includes(programType) && {
          headerChildren: (
            <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
              <Button variant="secondary" tone="neutral" size="sm" onClick={openExceptionModal}>
                {equipmentException?.equipmentTypes?.length
                  ? 'Modify Equipment Exception'
                  : 'Grant Equipment Exception'}
              </Button>
            </RequirePermissions>
          ),
        })}
    >
      <Box width="100">
        <Box childGap={{ base: 'lg', desktop: 'xl' }} direction="row">
          <Field
            label="Panel Manufacturer"
            name="panelManufacturer"
            id="panelManufacturer"
            options={panelManufacturerOptions}
            component={FormikSelectInputNative}
            isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
            maxWidth="3xl"
          />
          {useSearchableModelSelect ? (
            <SelectInput
              id="panelModel"
              name="panelModel"
              label="Panel Model"
              options={panelModelOptions}
              value={resolveSelectedOption(panelModelOptions, values.panelModel)}
              onChange={handlePanelModelChange}
              onBlur={handlePanelModelBlur}
              isClearable
              isDisabled={
                isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable || !values.panelManufacturer
              }
            />
          ) : (
            <Field
              label="Panel Model"
              name="panelModel"
              id="panelModel"
              component={FormikSelectInputNative}
              options={panelModelOptions}
              isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
            />
          )}
          {feocDcCalculator && panelVintageOptions.length > 1 && (
            <Field
              label="Panel Vintage"
              name="panelVintage"
              id="panelVintage"
              maxWidth="xl"
              component={FormikSelectInputNative}
              options={panelVintageOptions}
              isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
            />
          )}
          {!hideQuantity && (
            <Field
              label="Quantity"
              name="totalPanelCount"
              id="totalPanelCount"
              component={FormikTextInput}
              isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuantityValue('totalPanelCount', e)}
              maxWidth="lg"
            />
          )}
        </Box>
        <Box childGap={{ base: 'lg', desktop: 'xl' }} direction="row" margin={'lg 0 0 0'}>
          <Field
            label="Inverter Manufacturer"
            name="inverterManufacturer"
            id="inverterManufacturer"
            component={FormikSelectInputNative}
            options={inverterManufacturerOptions}
            isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
            maxWidth="3xl"
          />
          <Box childGap={{ base: 'lg', desktop: 'xl' }} width="100">
            <Box>
              <FieldArray name="inverters" render={renderInverterModels} />
            </Box>
            {allowMultipleInverters && (
              <Button
                variant="secondary"
                tone="neutral"
                onClick={addInverter}
                isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
                iconPrefix="add"
                size="sm"
              >
                inverter
              </Button>
            )}
          </Box>
        </Box>
        {accountId && EquipmentExceptionAllowedProgramTypes.includes(programType) && (
          <GrantEquipmentExceptionModal
            isOpen={isExceptionModalOpen}
            onDismiss={closeExceptionModal}
            onSubmit={handleGrantException}
            onRemove={handleRemoveException}
            existingTypes={equipmentException?.equipmentTypes}
            existingNote={equipmentException?.note}
          />
        )}
        {showEnphaseCheckboxes && enphaseInverterSelected && (
          <EnphaseAcknowledgmentCheckbox
            fieldName="enphaseInverterAck"
            componentType="inverter"
            isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
            onCheckboxChange={setFieldValue}
          />
        )}
        {showOptimizerCheckbox && solarEdgeSelected && (
          <Box childGap={{ base: 'lg', desktop: 'xl' }} direction="row" margin={'lg 0 0 0'}>
            <Field
              label={
                <>
                  I understand that to qualify for both DC and non-PFE value, we can install <strong>U650</strong>{' '}
                  and <strong>U650B</strong> Optimizers. To qualify for only Non-PFE value, we can also install{' '}
                  <strong>S500B</strong> optimizers with serial numbers beginning with SBxxxx, STxxxx, SVxxxx, SMxxxx
                  or SZxxxx or we can install <strong>S440</strong> optimizers with serial numbers beginning with
                  SBxxxx, STxxxx, SVxxxx, SMxxxx, SZxxxx and S4.
                </>
              }
              name="optimizerAck"
              id="optimizerAck"
              component={FormikCheckboxInput}
              isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
              onChange={(e: any) => setFieldValue('optimizerAck', e.target.checked)}
            />
          </Box>
        )}
        {showStorageOptions && (
          <>
            <Box childGap={{ base: 'lg', desktop: 'xl' }} margin={'xl 0 0 0'} direction="row">
              <Field
                label="Storage Manufacturer"
                name="storageManufacturer"
                id="storageManufacturer"
                options={storageManufacturerOptions}
                component={FormikSelectInputNative}
                isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
                maxWidth="3xl"
              />
              <Box childGap={{ base: 'lg', desktop: 'xl' }} width="100">
                <Box>
                  <FieldArray name="storageModels" render={renderStorageModels} />
                </Box>
                {allowMultipleBatteries && (
                  <Button
                    variant="secondary"
                    tone="neutral"
                    onClick={addStorage}
                    isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
                    iconPrefix="add"
                    size="sm"
                  >
                    storage
                  </Button>
                )}
              </Box>
            </Box>
            {showEnphaseCheckboxes && enphaseStorageSelected && (
              <EnphaseAcknowledgmentCheckbox
                fieldName="enphaseStorageAck"
                componentType="battery"
                isDisabled={isAnythingLoadingOrSubmitting || readOnly || !isPackageEditable}
                onCheckboxChange={setFieldValue}
              />
            )}
          </>
        )}
      </Box>
    </FormSection>
  );
};
